/**
 * Toggles the collection facets sidebar for the "custom" filter direction (desktop).
 */
class FacetsCustomToggle extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {MutationObserver | null} */
  #observer = null;
  #isOpen = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#button = this.querySelector('[data-facets-custom-toggle]');
    this.#pane = document.getElementById(this.dataset.paneId || '');
    this.#collectionWrapper = this.closest('.collection-wrapper');

    if (!(this.#button instanceof HTMLButtonElement) || !this.#pane) return;

    this.#isOpen = !this.#pane.classList.contains('hidden--desktop');

    this.#collectionWrapper?.classList.add('collection-wrapper--has-facets-custom');
    this.#button.addEventListener('click', () => this.#toggle(), { signal });
    this.#sync();

    this.#observer = new MutationObserver(() => this.#preserveState());
    if (this.#collectionWrapper) {
      this.#observer.observe(this.#collectionWrapper, { attributes: true, attributeFilter: ['class'] });
    }
    if (this.#pane) {
      this.#observer.observe(this.#pane, { attributes: true, attributeFilter: ['class'] });
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
  }

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLElement | null} */
  #pane = null;

  /** @type {HTMLElement | null} */
  #collectionWrapper = null;

  #preserveState() {
    if (!this.#button || !this.#pane) return;

    let changed = false;

    if (this.#collectionWrapper && !this.#collectionWrapper.classList.contains('collection-wrapper--has-facets-custom')) {
      this.#collectionWrapper.classList.add('collection-wrapper--has-facets-custom');
      changed = true;
    }

    if (this.#isOpen) {
      if (this.#collectionWrapper && !this.#collectionWrapper.classList.contains('collection-wrapper--facets-custom-open')) {
        this.#collectionWrapper.classList.add('collection-wrapper--facets-custom-open');
        changed = true;
      }
      if (this.#pane.classList.contains('hidden--desktop')) {
        this.#pane.classList.remove('hidden--desktop');
        changed = true;
      }
      if (!this.#pane.classList.contains('facets-block-wrapper--open')) {
        this.#pane.classList.add('facets-block-wrapper--open');
        changed = true;
      }
    } else {
      if (this.#collectionWrapper && this.#collectionWrapper.classList.contains('collection-wrapper--facets-custom-open')) {
        this.#collectionWrapper.classList.remove('collection-wrapper--facets-custom-open');
        changed = true;
      }
      if (!this.#pane.classList.contains('hidden--desktop')) {
        this.#pane.classList.add('hidden--desktop');
        changed = true;
      }
      if (this.#pane.classList.contains('facets-block-wrapper--open')) {
        this.#pane.classList.remove('facets-block-wrapper--open');
        changed = true;
      }
    }

    if (changed) {
      this.#sync();
    }
  }

  #toggle() {
    if (!this.#button || !this.#pane) return;

    this.#isOpen = !this.#isOpen;
    this.#preserveState();
  }

  #sync() {
    if (!this.#button) return;

    const isOpen = this.#pane != null && !this.#pane.classList.contains('hidden--desktop');
    this.#button.setAttribute('aria-expanded', String(isOpen));

    this.querySelectorAll('[data-facets-custom-toggle-label]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const showWhen = el.dataset.facetsCustomToggleLabel;
      const visible = (showWhen === 'open' && isOpen) || (showWhen === 'closed' && !isOpen);
      el.hidden = !visible;
    });

    this.querySelectorAll('[data-facets-custom-toggle-icon]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const showWhen = el.dataset.facetsCustomToggleIcon;
      const visible = (showWhen === 'open' && isOpen) || (showWhen === 'closed' && !isOpen);
      el.hidden = !visible;
    });
  }
}

if (!customElements.get('facets-custom-toggle')) {
  customElements.define('facets-custom-toggle', FacetsCustomToggle);
}
